import { type PlatformType } from '@cart-services/hooks/fp-b2b/store-map'
import { type CountryCode, type ProjectCode } from '@constants'
import { type CreditCardUpdateInfo, type PaymentSetting } from '@payments'
import { createContext, useMemo, useState, type PropsWithChildren } from 'react'
import { useConditionalRedirectWhenEntry } from '../../hooks/use-conditional-redirect-when-entry'

interface CheckoutContextValue extends CheckoutProviderProps {
  creditCardUpdateInfo: CreditCardUpdateInfo
  setCreditCardUpdateInfo: (value: CreditCardUpdateInfo) => void
  isInRetryPaymentPage: boolean
}

export const CheckoutContext = createContext<CheckoutContextValue | null>(null)

export interface CheckoutProviderProps {
  platform: PlatformType
  projectCode: ProjectCode
  currency: string
  countryCode: CountryCode
  language: string
  campaignId: number
  env: PaymentSetting['appEnv']
  campaignCode?: string
  basePath?: string
  fallbackUrl?: string
}

export const CheckoutProvider = (props: PropsWithChildren<CheckoutProviderProps>) => {
  const { children, platform, projectCode, countryCode, currency, language, campaignId, env, campaignCode, basePath, fallbackUrl } = props
  const isInRetryPaymentPage = window.location.pathname.includes('/retry-payment')
  const [creditCardUpdateInfo, setCreditCardUpdateInfo] = useState<CreditCardUpdateInfo>(null)

  // Redirect to fallbackUrl when cart is empty
  useConditionalRedirectWhenEntry(campaignId, platform, fallbackUrl)

  const contextValue = useMemo(
    () => ({
      platform,
      projectCode,
      currency,
      countryCode,
      language,
      campaignId,
      env,
      campaignCode,
      basePath,
      fallbackUrl,
      isInRetryPaymentPage,
      creditCardUpdateInfo,
      setCreditCardUpdateInfo,
    }),
    [platform, projectCode, currency, countryCode, language, campaignId, env, campaignCode, basePath, fallbackUrl, isInRetryPaymentPage, creditCardUpdateInfo],
  )

  return (
    <CheckoutContext.Provider value={contextValue}>
      {children}
    </CheckoutContext.Provider>
  )
}
